import { useState } from 'react'
import api from '../../utils/axiosConfig'

// Shown full-screen (blocking the rest of the app) when a reactivated user
// must change their password before they can access the system.
const ForcePasswordReset = () => {
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    setPasswords(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (passwords.new_password.length < 6) {
      setError('New password must be at least 6 characters')
      return
    }
    if (passwords.new_password !== passwords.new_password_confirmation) {
      setError('New password and confirmation do not match')
      return
    }
    if (passwords.new_password === passwords.current_password) {
      setError('New password must be different from your current password')
      return
    }

    setSubmitting(true)
    try {
      const response = await api.post('/user/change-password', passwords)
      if (response.data.success) {
        // Password reset complete — log out and send the user to the login page
        // so they sign in again with their new password.
        localStorage.removeItem('force_password_reset')
        localStorage.removeItem('tenant_admin_token')
        localStorage.removeItem('tenant_admin_user')
        alert('Password changed successfully. Please log in with your new password.')
        window.location.href = '/user-login'
      } else {
        setError(response.data.message || 'Failed to change password')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password')
    } finally {
      setSubmitting(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('tenant_admin_token')
    localStorage.removeItem('tenant_admin_user')
    localStorage.removeItem('force_password_reset')
    window.location.href = '/user-login'
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.iconWrap}>
          <i className="fas fa-shield-alt" style={{ fontSize: '1.6rem', color: '#c00f15' }}></i>
        </div>
        <h2 style={styles.title}>Password Reset Required</h2>
        <p style={styles.subtitle}>
          Your account was recently reactivated. For security, you must set a new
          password before you can access the system.
        </p>

        {error && <div style={styles.error}><i className="fas fa-exclamation-circle" style={{ marginRight: 6 }}></i>{error}</div>}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>Current Password</label>
          <input
            type="password"
            name="current_password"
            value={passwords.current_password}
            onChange={handleChange}
            placeholder="Enter current password"
            style={styles.input}
            required
          />

          <label style={styles.label}>New Password</label>
          <input
            type="password"
            name="new_password"
            value={passwords.new_password}
            onChange={handleChange}
            placeholder="At least 6 characters"
            style={styles.input}
            required
          />

          <label style={styles.label}>Confirm New Password</label>
          <input
            type="password"
            name="new_password_confirmation"
            value={passwords.new_password_confirmation}
            onChange={handleChange}
            placeholder="Re-enter new password"
            style={styles.input}
            required
          />

          <button type="submit" style={{ ...styles.submit, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>
            {submitting ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }}></i>Updating...</> : <><i className="fas fa-check" style={{ marginRight: 8 }}></i>Set New Password</>}
          </button>
        </form>

        <button type="button" onClick={handleLogout} style={styles.logout}>
          <i className="fas fa-sign-out-alt" style={{ marginRight: 6 }}></i>Logout
        </button>
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'linear-gradient(135deg, #fdf1f1 0%, #f8fafc 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
  },
  card: {
    width: '100%', maxWidth: '440px', background: '#fff', borderRadius: '14px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.12)', padding: '2rem', textAlign: 'center',
  },
  iconWrap: {
    width: '58px', height: '58px', borderRadius: '50%', background: '#fdeaea',
    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem',
  },
  title: { fontSize: '1.35rem', fontWeight: 700, color: '#111827', margin: '0 0 0.5rem' },
  subtitle: { fontSize: '0.9rem', color: '#6b7280', lineHeight: 1.5, margin: '0 0 1.25rem' },
  error: {
    background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
    borderRadius: '8px', padding: '10px 12px', fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'left',
  },
  label: { display: 'block', textAlign: 'left', fontSize: '0.82rem', fontWeight: 600, color: '#374151', margin: '0 0 0.35rem' },
  input: {
    width: '100%', padding: '0.65rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '8px',
    fontSize: '0.9rem', fontFamily: 'inherit', marginBottom: '1rem', boxSizing: 'border-box',
  },
  submit: {
    width: '100%', padding: '0.75rem', background: '#c00f15', color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', marginTop: '0.25rem',
  },
  logout: {
    marginTop: '1rem', background: 'none', border: 'none', color: '#6b7280',
    fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500,
  },
}

export default ForcePasswordReset
